import { RouteComponentProps, Router } from "@reach/router";
import React, { FunctionComponent, Suspense, useMemo } from "react";
import { ErrorBoundary } from "react-error-boundary";
import AdminPage from "../admin/AdminPage";
import AnimeViewingPage from "../anime-viewing/AnimeViewingPage";
import AnimePage from "../anime/AnimePage";
import DevLogPage from "../dev-log/DevLogPage";
import HentaiPage from "../hentai/HentaiPage";
import HomePage from "../home/HomePage";
import MusicPage from "../music/MusicPage";
import SettingsPage from "../settings/SettingsPage";
import ErrorFallback from "../shared/ErrorFallback";
import PageLoading from "../shared/PageLoading";
import SuperAdmin from "../super-admin/SuperAdminPage";
import InfoPage from "../info/InfoPage";
import HealthPage from "../health/HealthPage";
import AppsPage from "../apps/AppsPage";
import MangaPage from "../manga/MangaPage";
import FanboxPage from "../fanbox/FanboxPage";
import { NavigationBarModel } from "../navigation/NavigationNew";
import KitaharaPage from "../kitahara/KitaharaPage";
import TranslationPage from "../fanbox/translation/TranslationPage";
import HoardPage from "../hoard/HoardPage";
import TomoeToolsPage from "../fanbox/tools/ToolsPage";
import ChannelPage from "../channel/ChannelPage";

const NotFound: FunctionComponent<RouteComponentProps> = () => {
	return <h1>404 - Not Found</h1>;
}

const Loading: FunctionComponent<RouteComponentProps> = () => {
	return <PageLoading message="Loading page assets"/>;
}

interface IProps {
	loggedIn: boolean | undefined;
	theme: [string, (value: string) => void];
	pages: NavigationBarModel | undefined;
	refreshMenu(): void;
}

const Page: FunctionComponent<IProps> = (props) => {
	const { loggedIn, theme, pages, refreshMenu } = props;

	const loading = useMemo(() => {
		return <Loading />;
	}, []);

	if (!loggedIn) {
		return <ErrorBoundary FallbackComponent={ErrorFallback}>
			<Suspense fallback={loading}>
				<Router>
					<HomePage path="/" loggedIn={loggedIn} loading={loading} />
					<NotFound default />
				</Router>
			</Suspense>
		</ErrorBoundary>;
	}

	if (pages?.pages.find(p => p.path === "/Manga") && !pages?.pages.find(p => p.path === "/Anime")) {
		return <ErrorBoundary FallbackComponent={ErrorFallback}>
			<Suspense fallback={loading}>
				<Router>
					<FanboxPage path="/*articleId" loggedIn={Boolean(loggedIn)} loading={loading} />
					<FanboxPage path="Kitahara/*articleId" loggedIn={Boolean(loggedIn)} loading={loading} />
					<TranslationPage path="Manga/*mangaId" loggedIn={Boolean(loggedIn)} loading={loading} />
					<TomoeToolsPage path="Tools" loggedIn={Boolean(loggedIn)} loading={loading} />
					<SettingsPage path="Settings" loggedIn={Boolean(loggedIn)} theme={theme} refreshMenu={refreshMenu} />
					<NotFound default />
				</Router>
			</Suspense>
		</ErrorBoundary>;
	}

	return <ErrorBoundary FallbackComponent={ErrorFallback}>
		<Suspense fallback={loading}>
			<Router>
				<HomePage path="/" loggedIn={loggedIn} loading={loading} />
				<AnimePage path="Anime" loggedIn={Boolean(loggedIn)} />
				{/* <AnimeViewingPage path="Anime/:animeId" loggedIn={loggedIn} /> */}
				<AnimeViewingPage path="Anime/:animeId/*episodeId" loggedIn={Boolean(loggedIn)} />
				<HentaiPage path="Hentai" loggedIn={Boolean(loggedIn)} />
				{/* <AnimeViewingPage path="Hentai/:animeId" loggedIn={loggedIn} /> */}
				<AnimeViewingPage path="Hentai/:animeId/*episodeId" loggedIn={Boolean(loggedIn)} />
				<ChannelPage path="Channel/*channelId" loggedIn={Boolean(loggedIn)} />
				{/* <MusicPage path="Music" loggedIn={loggedIn} /> */}
				<MusicPage path="Music/*groupId" loggedIn={Boolean(loggedIn)} />
				<MusicPage path="Music/:groupId/:songId" loggedIn={Boolean(loggedIn)} />
				<MangaPage path="Manga" loggedIn={Boolean(loggedIn)} />
				<HoardPage path="Hoard" loggedIn={Boolean(loggedIn)} />
				<AdminPage path="Admin" loggedIn={Boolean(loggedIn)} />
				<SuperAdmin path="SuperAdmin" loggedIn={Boolean(loggedIn)} />
				<DevLogPage path="DevLog" />
				<InfoPage path="Info" />
				<FanboxPage path="Tomoe/*articleId" loggedIn={Boolean(loggedIn)} loading={loading} />
				<TranslationPage path="TomoeManga/*mangaId" loggedIn={Boolean(loggedIn)} loading={loading} />
				<TomoeToolsPage path="TomoeTools" loggedIn={Boolean(loggedIn)} loading={loading} />
				<AppsPage path="Apps" loggedIn={Boolean(loggedIn)} />
				<HealthPage path="Health" loggedIn={Boolean(loggedIn)} />
				<SettingsPage path="Settings" loggedIn={Boolean(loggedIn)} theme={theme} refreshMenu={refreshMenu} />
				<NotFound default />
			</Router>
		</Suspense>
	</ErrorBoundary>;
}

export default Page;